import './RecursivePacingBar.scss';
import cn from 'classnames';
import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { MdAutoGraph, MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md';
import { Button } from '@Components/Button/Button';
import { DownloadDropdown } from '@Components/DownloadDropdown/DownloadDropdown';
import { Dropdown } from '@Components/Dropdown/Dropdown';
import { DropdownToggleType } from '@Components/Dropdown/dropdown-constants';
import { GraphTableToggleButton } from '@Components/GraphTableToggleButton/GraphTableToggleButton';
import { SegmentedControlButton } from '@Components/SegmentedControlButton';
import { IconButton } from '@Components/IconButton/IconButton';
import { Design, Placement, Size } from '@Components/designConstants';
import { DateGroupingOption } from '@src/utils/date-utils';
import { downloadPNG } from '@src/utils/download-utils';
import { ModalCumulativeAndDailyGraph } from './ModalCumulativeAndDailyGraph';
import { ModalCumulativeAndDailyTable } from './ModalCumulativeAndDailyTable';
import { ModalCustomLegend } from './ModalCustomLegend';
import { PacingBar } from './PacingBar';
import { Simplicity } from '../pacing-constants';
import { isBarsSegment, isPacingRatioGoalType } from '../pacingUtils';

const DATE_GROUPING_OPTIONS: DateGroupingOption[] = [{ value: 'Day', label: 'Daily' }];

interface RecursivePacingBarProps {
  pacingBarsData: Record<string, any>;
  start: string;
  end: string;
  simplicity: string;
  setSimplicity: Dispatch<SetStateAction<string>>;
  sortOption: string;
  goalKey: string;
  longestText: number;
  goalName: string;
  isActual: boolean;
  activeDimensions: string[];
  maxGoal?: number;
  segmentName?: string;
  level?: number;
  isTotal?: boolean;
}

export const RecursivePacingBar = ({
  pacingBarsData,
  start,
  end,
  goalKey,
  sortOption,
  simplicity,
  setSimplicity,
  longestText,
  goalName,
  isActual,
  activeDimensions,
  maxGoal = -1,
  segmentName = 'N/A',
  level = 0,
  isTotal = false
}: RecursivePacingBarProps) => {
  const [activeModal, setActiveModal] = useState(false);
  const [displayMode, setDisplayMode] = useState('Cumulative');
  const [graphView, setGraphView] = useState(true);
  const [chartDateGrouping, setChartDateGrouping] = useState('Day');
  const [showChildSegments, setShowChildSegments] = useState(true);

  const pngDownloadPacingData = useCallback(async () => {
    await downloadPNG('.modal-body .pacing-modal-content', 'pacingChart');
  }, []);

  const { currentSegment } = pacingBarsData;

  const numberOfSegments = useMemo(
    () => activeDimensions.length - level,
    [activeDimensions, level]
  );

  const { formattedData, childrenData } = useMemo(() => {
    const omitted = Object.fromEntries(
      Object.entries(pacingBarsData).filter(([key, value]) => isBarsSegment(key, value))
    );
    const bookedKey = simplicity === Simplicity.SIMPLE ? 'booked' : 'adjustedBooked';
    const deliveryTotalKey =
      simplicity === Simplicity.SIMPLE ? 'simpleDeliveryTotal' : 'adjustedDeliveryTotal';
    const projectedFutureKey =
      simplicity === Simplicity.SIMPLE ? 'simpleProjectedFuture' : 'adjustedProjectedFuture';
    const remainderTotalKey =
      simplicity === Simplicity.SIMPLE ? 'simpleRemainderTotal' : 'adjustedRemainderTotal';
    return {
      formattedData: {
        segmentName,
        budget: pacingBarsData[bookedKey] ? pacingBarsData[bookedKey][goalKey] : 0,
        totalCleared: pacingBarsData[deliveryTotalKey]
          ? pacingBarsData[deliveryTotalKey][goalKey]
          : 0,
        totalProjected: pacingBarsData[projectedFutureKey]
          ? pacingBarsData[projectedFutureKey][goalKey]
          : 0,
        totalRemainder: pacingBarsData[remainderTotalKey]
          ? pacingBarsData[remainderTotalKey][goalKey]
          : 0
      },
      childrenData: omitted as Record<string, any>
    };
  }, [pacingBarsData, goalKey, simplicity, segmentName]);

  const maxChildGoal = useMemo(
    // A targetless (ratio-goalless) child omits `booked` entirely; treat it as 0 here so it still
    // renders its actual instead of crashing the max.
    () =>
      Math.max(...Object.keys(childrenData).map(key => childrenData[key].booked?.[goalKey] ?? 0)),
    [childrenData, goalKey]
  );

  const children = useMemo(() => {
    let sortedData = childrenData;

    switch (sortOption) {
      case 'Abc':
        sortedData = Object.entries(childrenData)
          .map(([key, val]) => [key, [key, val]])
          .sort(([a], [b]) =>
            a.toString().localeCompare(b.toString(), undefined, { sensitivity: 'base' })
          );
        break;
      case 'goalValue':
        // Sort the entries directly by delivery total (descending); `?.` guards a targetless node
        // with no simpleDeliveryTotal.
        sortedData = Object.entries(childrenData)
          .map(([key, val]) => [key, [key, val]])
          .sort(
            ([, a], [, b]) =>
              (b[1].simpleDeliveryTotal?.[goalKey] ?? 0) -
              (a[1].simpleDeliveryTotal?.[goalKey] ?? 0)
          );
        break;
      default:
        break;
    }
    return sortedData.map(([, pair]) => {
      const segment = pair[0];
      const data = pair[1];
      if (data.booked && data.booked[goalKey] == null) {
        return null;
      }
      return (
        <div key={segment} className={cn('pacing-bar-group', `level-${level}`)}>
          <RecursivePacingBar
            pacingBarsData={data}
            start={start}
            end={end}
            level={level + 1}
            sortOption={sortOption}
            segmentName={segment}
            simplicity={simplicity}
            setSimplicity={setSimplicity}
            maxGoal={maxChildGoal}
            goalKey={goalKey}
            longestText={longestText}
            isActual={isActual}
            goalName={goalName}
            activeDimensions={activeDimensions}
          />
        </div>
      );
    });
  }, [
    childrenData,
    maxChildGoal,
    level,
    sortOption,
    activeDimensions,
    end,
    goalKey,
    goalName,
    isActual,
    longestText,
    simplicity,
    setSimplicity,
    start
  ]);

  return (
    <div
      className={cn(
        'pacing-bar-container',
        { consolidated: activeDimensions.length === 1 },
        `level-${level}`
      )}
    >
      <div className={cn('pacing-bar-wrapper', `level-${level}`)}>
        {level === 1 && activeDimensions.length > 1 && (
          <div className='charts-container-body-header'>
            <div className='container-body-header-title'>
              {segmentName.includes(',') ? segmentName.split(',').join(', ') : segmentName}
            </div>
            <IconButton onClick={() => setShowChildSegments(!showChildSegments)}>
              {showChildSegments ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />}
            </IconButton>
          </div>
        )}

        <div className='pacing-bar-inner-wrapper'>
          <PacingBar
            maxGoal={maxGoal}
            pacingData={formattedData}
            marginLeft={level}
            longestText={longestText}
            isActual={isActual}
            goalName={goalName}
            numberOfSegments={numberOfSegments}
            currentSegment={currentSegment}
          />
          <div>
            <Button
              size={Size.LG}
              design={Design.NEUTRAL}
              leadingElement={<MdAutoGraph />}
              onClick={() => setActiveModal(true)}
            >
              Details
            </Button>
            {activeModal && (
              <Modal show onHide={() => setActiveModal(false)} size='xl' className='pacing-modal'>
                <Modal.Header closeButton>
                  <div className='pacing-modal-header'>
                    <div className='pacing-modal-header-buttons'>
                      <Dropdown
                        overlayPlacement={Placement.RIGHT}
                        size={Size.LG}
                        type={DropdownToggleType.WIDGET_TITLE}
                        value={chartDateGrouping}
                        options={DATE_GROUPING_OPTIONS}
                        onChange={setChartDateGrouping}
                      />
                      {isTotal ? '' : segmentName}
                      {` ${goalName}`}
                    </div>
                    <div className='pacing-modal-header-buttons'>
                      <SegmentedControlButton
                        options={graphView ? ['Cumulative', 'Daily'] : ['Daily']}
                        selectedOption={graphView ? displayMode : 'Daily'}
                        onChange={setDisplayMode}
                      />
                      {/* Ratio goals only show actuals + goal values, so Simple/Adjusted has no
                            effect — hide it (mirrors the bars header). */}
                      {!isPacingRatioGoalType(goalName) && (
                        <SegmentedControlButton
                          options={[Simplicity.SIMPLE, Simplicity.ADJUSTED]}
                          selectedOption={simplicity}
                          onChange={setSimplicity}
                        />
                      )}
                      <GraphTableToggleButton
                        size={Size.MD}
                        graphView={graphView}
                        setGraphView={setGraphView}
                        widgetName='pacing_bars_dashboard'
                      />
                      <DownloadDropdown
                        onClickOptions={[pngDownloadPacingData]}
                        menuOptions={['PNG']}
                      />
                    </div>
                  </div>
                </Modal.Header>
                <Modal.Body>
                  {graphView ? (
                    <div className='pacing-modal-content'>
                      <PacingBar
                        pacingData={formattedData}
                        marginLeft={level}
                        longestText={longestText}
                        isActual={isActual}
                        goalName={goalName}
                        numberOfSegments={numberOfSegments}
                        isModal
                      />
                      <div className='pacing-modal-chart-row'>
                        <div className='pacing-modal-chart'>
                          <ModalCumulativeAndDailyGraph
                            data={pacingBarsData}
                            start={start}
                            end={end}
                            showCumulative={displayMode === 'Cumulative'}
                            simplicity={simplicity}
                            goalName={goalName}
                          />
                        </div>
                        <ModalCustomLegend
                          graphView={displayMode === 'Cumulative'}
                          goalName={goalName}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className='pacing-modal-content table-view'>
                      <ModalCumulativeAndDailyTable
                        data={pacingBarsData}
                        goalName={goalName}
                        start={start}
                        end={end}
                        simplicity={simplicity}
                      />
                    </div>
                  )}
                </Modal.Body>
              </Modal>
            )}
          </div>
        </div>
      </div>
      {showChildSegments && children}
    </div>
  );
};
